import { startAuthentication } from '@simplewebauthn/browser';
import type {
	AuthenticationResponseJSON,
	PublicKeyCredentialCreationOptionsJSON,
} from '@simplewebauthn/typescript-types';
import { camelizeKeys, decamelizeKeys } from 'humps';
import React, { useEffect } from 'react';
import { useHistory } from 'react-router-dom';

import useTriggeredRequest from '@api/hooks/useTriggeredRequest';
import AccountClientRequests from '@api/requests';

import { showToast } from '@app/providers/ToastProvider';
import { useAppDispatch, useAppSelector } from '@app/redux/hooks';
import { mfaActions } from '@app/redux/mfa/mfa.reducer';
import { getMFAPreconditionData } from '@app/redux/mfa/mfa.selectors';
import RoutePaths from '@app/routes/paths';
import useGenerateRoutePath from '@app/routes/paths/useGenerateRoutePath';

import { Grid } from '@mui/material';

import Loader from '@components/loader';

import MFA_METHODS_ENUM from '@pages/Verification/MFASetupPage/enums/MFA_METHODS.enum';

import { use2FALogin } from '../../use2FALogin';

const WebAuthn2FA = ({ onDecline }: { onDecline?: () => void }) => {
	const { startRequest } = useTriggeredRequest(AccountClientRequests.MFA.loginWebAuthnMethod);
	const { login } = use2FALogin({ method: MFA_METHODS_ENUM.WEBAUTHN });
	const mfaPreconditionData = useAppSelector(getMFAPreconditionData);
	const generateRoutePath = useGenerateRoutePath();
	const dispatch = useAppDispatch();
	const history = useHistory();

	useEffect(() => {
		const handleRequest = async () => {
			if (mfaPreconditionData) {
				try {
					const publicKey = await startRequest({ email: mfaPreconditionData.email }).then(
						({ public_key }) => camelizeKeys(public_key) as PublicKeyCredentialCreationOptionsJSON,
					);
					const code = await startAuthentication(publicKey).then(code => {
						const decamelizeCode = decamelizeKeys(code) as AuthenticationResponseJSON;
						return {
							...decamelizeCode,
							response: { ...decamelizeCode.response, client_data_json: code.response.clientDataJSON },
						};
					});
					await login({ code });
				} catch (error) {
					if (error instanceof Error && error.name === 'NotAllowedError') {
						if (onDecline) onDecline();
						else {
							dispatch(mfaActions.clearMFAPreconditionData());
							history.push(generateRoutePath(RoutePaths.AUTH.LOGIN));
						}
					} else showToast.error('Something went wrong!');
				}
			}
		};
		handleRequest();
	}, []);

	return (
		<Grid container justifyContent={'center'}>
			<Loader />
		</Grid>
	);
};

export default WebAuthn2FA;
